import { BiometryType } from '@eika-infrastruktur/mobilbank-lib';
import { SigningMethodType } from '@eika-infrastruktur/mobilbank-lib/src/nativeProps';
import { DateInputErrors } from '@eika/date-input';

import { EndType } from '../components/shared/DateField/EndDateType';
import {
    AccountType,
    AccountWithdrawalPeriod,
    AppError,
    Category,
    ChargeBearerOptions,
    CrossBorderAccountType,
    CrossBorderPriority,
    CustomerRole,
    EfakturaPermissionStatus,
    EfakturaType,
    InvoiceStatus,
    InvoiceType,
    LoanApplicationStatus,
    LoanApplicationType,
    NotificationType,
    PaymentInterval,
    PaymentItemStatus,
    PaymentSubStatus,
    PaymentType,
    ReceiptType,
    RecurringPaymentStatus,
    RecurringPaymentType,
    TietoEvryEraEsbWsError,
    TransactionCategory,
    TransactionStatus,
    TransactionType,
} from '../generated-types';
import { SSNErrorType } from '../utils/validation/validateSSN';
import { AmountSelectedType } from '../widgets/EfakturaSharing/form/state';
import { BatchEFakturaErrorMessages } from '../widgets/MultipleApprovalEFakturaList/util/validate';
import type { TaskManagerType } from '../widgets/TaskManager/TaskManagerTypes';

interface PaymentInfoMessage {
    success: string;
    successPlural: string;
    successFuture: string;
    error: {
        heading: string;
        description: string;
        check: string;
        checkFuturePayments: string;
    };
    notSigned: {
        heading: string;
        description: string;
        tryAgain: string;
        change: string;
    };
    notCompleted: {
        title: string;
        description: string;
        continue: string;
        change: string;
    };
}

interface StopFuturePaymentInfoMessage {
    success: string;
    error: {
        heading: string;
        description: string;
        check: string;
    };
}

interface UpdateFuturePaymentInfoMessage {
    success: string;
    error: {
        heading: string;
        description: string;
        check: string;
    };
    notSigned: {
        heading: string;
        description: string;
        tryAgain: string;
        change: string;
        cancel: string;
    };
}

interface DeleteAvtaleGiroInfoMessage {
    success: string;
    error: {
        heading: string;
        description: string;
        check: string;
    };
}

export enum NoteTypes {
    MESSAGE = 'MESSAGE',
    KID = 'KID',
    KID_OR_MESSAGE = 'KID_OR_MESSAGE',
}

export default interface TranslationsConfigType {
    unavailableAccountsTitle: string;
    loans: {
        arrearsLabel: string;
        arrearsBalance: string;
        extraDownpayment: string;
        infoTextDownPayment: string;
        arrearsWarning: {
            description: string;
            buttonText: string;
        };
        seeOurLoans: string;
    };
    newLoan: {
        title: string;
        description: string;
    };
    aboutApp: {
        version: string;
        thirdPartyLicenses: string;
        privacy: string;
    };
    accountNotFound: {
        noAccount: string;
        toFrontPage: string;
    };
    accountOverview: {
        headingMobile: string;
        errorMessageCards: string;
    };
    balance: string;
    availableForUse: string;
    accountViewSeeFuturePayments: string;
    accountViewErrorMessage: string;
    accountSkipLink: string;
    accountsEmptyInfo: {
        title: string;
        paymentAgreementTitle: string;
        recurringPaymentTitle: string;
    };
    add: string;
    advisor: {
        title: string;
        tlf: string;
        mail: string;
        sendEmail: string;
        call: string;
    };
    avtaleGiroProposals: {
        title: string;
        update: string;
        delete: string;
        new: string;
        sureYouWantToDelete: string;
        deletemessages: DeleteAvtaleGiroInfoMessage;
        proposalToAvtalegiro: string;
        onRepeatingEFaktura: string;
    };
    avtaleGiroAgreement: {
        deleteDialogue: string;
        skipLink: string;
        title: string;
        lastPayment: string;
        newAvtaleGiro: string;
        retriveAvtaleGiro: string;
        downloadAvtaleGiro: string;
        noAvtalegiro: string;
        createTitle: string;
        createDescription: string;
        kidField: string;
        kidPlaceholder: string;
        maxAmount: string;
        limitAmountParagraph: string;
        limitAmountText: string;
        limitAmountDescription: string;
        limitAmountPlaceholder: string;
        monthlyLimitAmountText: string;
        monthlyLimitAmountDescription: string;
        monthlyLimitAmountPlaceholder: string;
        combined: string;
        editMaxAmount: string;
        noteText: string;
        notePlaceholder: string;
        ownText: string;
        ownTextPlaceholder: string;
        amountLimitPlaceholder: string;
        deleteAvtaleGiroInfoMessages: DeleteAvtaleGiroInfoMessage;
        redirectLink: {
            header: string;
            description: string;
            link: string;
        };
        update: string;
        delayMessage: string;
        blocked: string;
        avtaleGiroUpdated: string;
        avtaleGiroWasStoppedPayment: string;
        avtaleGiroStoppedPaymentSendAgain: string;
    };
    avtalegiroValidation: {
        emptyKID: string;
        missingReceiver: string;
        invalidToAccount: string;
        existingAvtalegiroAgreement: string;
        createSuccess: string;
        updateSuccess: string;
        amountTooHigh: string;
        transactionLimitIsZero: string;
        monthlyLimitIsZero: string;
        monthlyMaxAmountMustBeLargerThanTransactional: string;
        transactionLimitMustBeLessThanMonthly: string;
        error: {
            heading: string;
            description: string;
            check: string;
        };
        notSigned: {
            heading: string;
            description: string;
            tryAgain: string;
            change: string;
            cancel: string;
        };
        notSignedUpdate: {
            heading: string;
            description: string;
            tryAgain: string;
            change: string;
            cancel: string;
        };
        rejected: {
            heading: string;
            description: string;
            tryAgain: string;
        };
    };
    approvalItemType: Record<string, string>;
    eFakturaItemType: Record<EfakturaType, string>;
    taskManager: {
        emptyState: string;
        noTasks: string;
        multipleErrors: {
            title: string;
            description: string;
            and: string;
        };
        errors: Record<TaskManagerType, string>;
    };
    approve: string;
    balanceAfterPayment: string;
    birthdayMessage: {
        title: string;
        title18: string;
        description: string;
        description18: string;
        linkText18: string;
        buttonText: string;
    };
    biometricType: Record<BiometryType, string>;
    bsuAccount: string;
    bsuDetails: {
        displayYearly: string;
        displayTotal: string;
        amountSaved: string;
        saldo: string;
        maximumAmount: string;
        interestThisYear: string;
        infoMessage: string;
        ageWarningMessage: string;
        interest: string;
        accruedInterest: string;
        bsuMaxAmountReached: string;
        maxAmountThisYear: string;
        hasRecurringPaymentInfoCardTitle: string;
        hasRecurringPaymentInfoCardText: string;
        noRecurringPaymentInfoCardTitle: string;
        noRecurringPaymentInfoCardText: string;
    };
    cancel: string;
    cancelPaymentDialogue: {
        title: string;
        closeDialogue: string;
    };
    chooseAnotherAccount: string;
    chooseOperation: string;
    close: string;
    closeAccount: {
        title: string;
        transferTo: string;
        chooseAccount: string;
        previousButton: string;
        nextButton: string;
        confirmTitle: string;
        confirmText: string;
        confirmButton: string;
        doneButton: string;
        success: string;
        cannotDelete: string;
        contactUs: string;
        contactUsButton: string;
        closeRecurringPayment: string;
        closeRecurringPaymentDescription: string;
        closeRecurringPaymentLink: string;
        closeCardTitle: string;
        closeCardText: string;
        closeCardDescription: string;
        card: string;
        cancel: string;
        cardOwnerSuffix: string;
    };
    creditCard: string;
    customerRole: Record<CustomerRole, string>;
    payToAForeignCountry: string;
    crossBorderReceiverListTitle: string;
    crossBorderReceiverUpdateMessage: string;
    crossBorderPayment: {
        calculatedExchangeRate: string;
        exchangeRate: string;
        numbersInNameInfoMessage: string;
        needOneCharacter: string;
        amountSubTitle: string;
        currencyExchangeError: string;
        label: Record<CrossBorderAccountType, string>;
        bic: string;
        addBic: string;
        requiredBicMessage: string;
        statusTitle: string;
        statusDescription: string;
        statusButton: string;
        desktopAmountTitle: string;
        domesticPaymentWarning: string;
        kinaInfo: string;
        messageReceiver: string;
        accountList: {
            title: string;
            description: string;
            priceListLink: string;
            noAccountsTitle: string;
            noAccountsDescription: string;
            createAccountLink: string;
            payFrom: string;
            label: string;
            button: string;
            errorMessage: string;
        };
        categoryList: {
            title: string;
            label: string;
            showMore: string;
            showLess: string;
            error: string;
            errorMessage: string;
        };
        description: {
            title: string;
            placeholder: string;
            descriptionLabel: string;
            messageLabel: string;
            descriptionError: string;
            messageError: string;
            tooManyCharacters: string;
        };
        newReceiver: {
            title: string;
            subTitle: string;
            creditorInfo: string;
            receiverNameLabel: string;
            createNewReceiverTab: string;
            countryLabel: string;
            countryPlaceholder: string;
            ibanLabel: string;
            chooseBetween: string;
            bicLabel: string;
            bbanLabel: string;
            nationalIdLabel: string;
            addressPlaceholder: string;
            address: string;
            receiverAddress: string;
            receiverBank: string;
            receiverCountry: string;
            postalCode: string;
            city: string;
            bankCountry: string;
            errorCountryTitle: string;
            errorCountry: string;
            errorCountryDetails: string;
            errorReceiverList: string;
            errorBankValidation: string;
            emptyBankValidation: string;
            errorValidToPayReceiver: string;
            errorIban: string;
            errorIbanValidation: string;
            errorValidation: string;
            errorIbanCountryCode: string;
            missingAccountNumber: string;
            validForPaymentError: string;
            accountNumberError: string;
            bicError: string;
            missingBic: string;
            missingBankBic: string;
            nationalIdError: string;
            missingNationalId: string;
            missingCreditorDetails: string;
            missingPostCode: string;
            missingTown: string;
            receiverNameError: string;
            selectedCountryError: string;
            paymentOptions: string;
            bankDetailsError: string;
        };
        summary: {
            title: string;
            payFrom: string;
            payTo: string;
            payToCountry: string;
            closeSummary: string;
            showSummary: string;
            receiver: string;
            amount: string;
            amountDateTitle: string;
            messageAndDescription: string;
            messageToReceiver: string;
            category: string;
            chosenCategory: string;
            errorDescription: string;
            newPaymentButton: string;
            fee: string;
            feeMessage: string;
            feeDescription: string;
            validationError: string;
            paymentPeriod: string;
            blockedErrorMessage: string;
            invalidAccountNumber: string;
        };
        invalidCurrency: string;
    };
    currencyField: {
        label: string;
        title: string;
        searchPlaceholder: string;
        description: string;
        recommended: string;
        allCurrency: string;
        transferCurrencyDescription: string;
        transferCurrencySubTitle: string;
        errorTransferCurrency: string;
        missingTransferCurrency: string;
    };
    chargeBearerField: {
        title: string;
        description: string;
        infoMessage: string;
    };
    chargeBearerOptions: Record<ChargeBearerOptions, string>;
    chargeBearerFeeDescription: Record<ChargeBearerOptions, string>;
    chargeBearerSelectOptions: Record<ChargeBearerOptions, string>;
    delete: string;
    details: string;
    disableAvtaleGiroChanges: {
        changeAndDeleteUnavailable: string;
        creationUnavailableTitle: string;
        creationUnavailableDescription: string;
    };
    confirmDelete: string;
    date: string;
    editDisplayAndOrderOfAccounts: string;
    editAccounts: {
        view: string;
        info: string;
        header: string;
        addNewGroup: string;
        remove: string;
        change: string;
        moveUp: string;
        moveDown: string;
        technicalError: string;
        errorNotSaved: string;
        accountsError: string;
        cantDisable: string;
        success: string;
        confirm: string;
        newGroupName: string;
    };
    eFaktura: {
        eFaktura: string;
        eFakturaApproved: string;
        eFakturaChangedDate: string;
        eFakturaError: string;
        eFakturaIcon: string;
        isDeleted: string;
        sureYouWantToDelete: string;
        willBeDeletedInAllBanks: string;
        noValidFromAccounts: string;
        seeAll: string;
        allEFakturasApproved: string;
        approve: string;
        eFakturaInvalidAccount: string;
        missingKidIssue: string;
        emptyList: string;
        notice: Record<EfakturaType, string | undefined>;
        confirmPayment: string;
        confirmationDialogTitle: string;
        nextEFaktura: string;
        previousEFaktura: string;
        delete: string;
    };
    eFakturaAgreement: {
        title: string;
        jttaTitle: string;
        jttaDescription: string;
        jttaAriaLabel: string;
        dialogTitle: string;
        dialogDescription: string;
        dialogConfirmation: string;
        warningMessage: string;
        errorLabel: string;
        noAgreementTitle: string;
        noAgreementDescription: string;
        missingContactDescription: string;
        noAgreementCaption: string;
        createAgreement: string;
        emailRequired: string;
        mobilNumberRequired: string;
        emailValidation: string;
        mobileNumberNotValid: string;
        update: string;
        emailField: {
            label: string;
            placeholder: string;
        };
        mobileNumberField: {
            label: string;
            placeholder: string;
        };
        historyLink: string;
        archiveLink: string;
        reservationsLink: string;
        notificationsLink: string;
    };
    eFakturaHistory: {
        title: string;
        description: string;
        error: string;
        noCreditors: string;
    };
    eFakturaNotifications: {
        toggleEmailLabel: string;
        off: string;
        noAgreementInfo: string;
        goBack: string;
        differentEmailWarning: {
            header: string;
            text: string;
            linkText: string;
        };
    };
    eFakturaReservation: {
        title: string;
        description: string;
        error: string;
        noCreditors: string;
        searchCreditor: string;
        noBlockedCreditors: string;
        yourReservations: string;
        unblockError: string;
        confirmUnblock: string;
        blockSuccess: string;
        blockError: string;
        youChose: string;
        blockDescription: string;
        confirmBlock: string;
    };
    eFakturaAccount: {
        title: string;
        description: string;
        label: string;
        success: string;
        chosenEFakturaAccount: string;
        error: string;
        notSetTitle: string;
        notSetDescription: string;
        unselectedSuccess: string;
        noAccountsTitle: string;
        noAccountsText1: string;
        noAccountsText2: string;
    };
    eFakturaSharing: {
        shareEfaktura: string;
        giveAnotherCustomerAccess: string;
        shareYourEfaktura: string;
        sharingWithNobody: string;
        sharingWith: string;
        sharingWithYou: string;
        sharingWithPlaceholder: string;
        andMore: string;
        whoDoYouWantToShareWith: string;
        name: string;
        writeName: string;
        ssn: string;
        writeSSN: string;
        howManyEfakturasToShare: string;
        amountSelected: Record<AmountSelectedType, string>;
        chooseEfakturasToShare: string;
        noEfakturas: string;
        errorFetchingEfaktura: string;
        userAlreadyInGiven: string;
        editSharing: string;
        deleteSharing: string;
        allEfaktura: string;
        confirmDelete: string;
        confirmDeleteDescription: string;
        receiverDeleted: string;
        userNotFound: string;
        chooseFromCompanies: string;
        orgNumber: string;
        shortSummary: string;
        confirmationText: Record<AmountSelectedType, string>;
        organisationNumber: string;
        deleteCompanyFromShareList: string;
        summaryNotice: string;
        giveAccess: string;
        validationErrors: {
            userNotFound: string;
            ssn: Record<SSNErrorType, string>;
            name: {
                required: string;
                error: Record<Exclude<EfakturaPermissionStatus, 'OK'>, string>;
            };
            amountSelected: {
                required: string;
                alreadySharedEverything: string;
            };
            selected: {
                tooFewSelected: string;
            };
        };
        noSharedEfakturaInfo: {
            header: string;
            noReceivedBody: string;
            noGivenBody: string;
        };
    };
    eFakturaEmpty: {
        title: string;
        description: string;
        link: string;
    };
    effectiveInterestInfo: {
        title: string;
        description1: string;
        description2: string;
    };
    forceUpdateModal: {
        title: string;
        description: string;
        buttonText: string;
    };
    pushPopup: {
        title: string;
        description: string;
        buttonText: string;
        notNow: string;
    };
    futurePaymentDetails: {
        normal: {
            noEditWarning: string;
        };
        foreignPayment: {
            noEditWarning: string;
        };
        noReject: {
            editWarning: string;
        };
        recurringPayment: {
            editWarning: string;
            editLink: string;
        };
        fakedLoanPayment: {
            warning: string;
        };
        calculatedAmount: string;
        amountInCurrency: string;
        paidBy: string;
        change: string;
    };
    minAmountToPay: string;
    errors: ErrorsConfigType;
    createdBy: string;
    you: string;
    paymentsForApproval: string;
    signAndAddApproval: string;
    approveAndPay: string;
    needsAdditionalApproval: string;
    outbox: {
        approvePayment: string;
        removeApproval: string;
        paymentsYouCanSignDomestic: string;
        paymentsYouCanSignCrossBorder: string;
        paymentsOthersMustSign: string;
        deleteApprovalItemFailed: string;
        deleteApprovalItemSuccess: string;
        unapproveItemFailed: string;
        unapproveItemSuccess: string;
        confirmUnapprove: string;
        emptyList: string;
        mainPageLink: string;
        dateSetToToday: string;
        edit: string;
        editOrDelete: string;
        editPayment: string;
        editSuccess: string;
        deleteError: string;
        errorContactUs: string;
        editError: string;
        retryEdit: string;
        delete: string;
        deletePayment: string;
        saveChanges: string;
        checkboxLabel: string;
        waitingForNrOfSignings: string;
        youHaveApproved: string;
        plannedPaid: string;
        totalForYourSigning: string;
        totalForOthersApproval: string;
        noSelectedPayments: string;
        paidBy: string;
        removeChosen: string;
        chooseAll: string;
    };
    approveAll: string;
    approveAllPaymentLimitValidation: string;
    from: string;
    futurePayments: {
        balanceToday: string;
        noFuturePayments: string;
        thisAccount: string;
        rightNow: string;
        infoMessage: string;
        infoAvtalegiroText: string;
        infoAvtalegiroLink: string;
        warningBMMissingFilesText: string;
        warningBMMissingFilesLink: string;
        paidBy: string;
        approvedBy: string;
    };
    futurePaymentsList: {
        ariaLabel: string;
        title: string;
        future: string;
        payment: string;
        payments: string;
        paymentsLeftThisMonth: string;
        removeAllSelected: string;
        selectAll: string;
        removeChosenAccounts: string;
        chooseAccount: string;
        chosenAccount: string;
        chosenAccounts: string;
        choose: string;
        total: string;
        totalIn: string;
        totalOut: string;
        noHits: string;
        couldNotGetFuturePayments: string;
    };
    futurePaymentsEFaktura: {
        it: string;
        theFirst: string;
        hasDeadlineToday: string;
        hasDeadlineTomorrow: string;
        hasDeadlineOn: string;
        hasDeadline: string;
        hadDeadlineYesterday: string;
        hadDeadlineLast: string;
        hadDeadline: string;
        youHave: string;
        newSingular: string;
        newPlural: string;
    };
    paymentItemStatus: Record<PaymentItemStatus, string>;
    paymentSubStatus: Partial<Record<PaymentSubStatus, string>>;
    insufficientFundsFromOwnAccount: string;
    futurePaymentType: Record<PaymentType, string>;
    futurePaymentOwnerSuffix: string;
    stopFuturePayment: {
        delete: string;
        confirm: string;
        cancel: string;
        header: string;
        headerParamTransfer: string;
        headerParamPayment: string;
        headerParamEFaktura: string;
    };
    futurePaymentsInfoCardTitle: string;
    futurePaymentsErrorMessage: string;
    futurePaymentTitle: {
        eFaktura: string;
        payment: string;
        transfer: string;
    };
    stoppedPayments: {
        title: string;
        error: string;
        empty: string;
        label: Record<PaymentSubStatus, string>;
        detailedDescription: Record<PaymentSubStatus, string>;
        exeedingMaxLimitAvtalegiro: string;
        exeedingMaxLimitAvtalegiroNotFound: string;
        editPaymentLimitsHere: string;
        goToAccount: string;
        findAvtalegiroHere: string;
    };
    immediatePayment: {
        title: string;
        info: string;
        priceInfo: string;
        priceButton: string;
        stepUpRequired: {
            title: string;
            text1: string;
            text2: string;
            text3: string;
            start: string;
            back: string;
        };
    };
    invalidMessage: string;
    isPaidFrom: string;
    keepAlive: {
        title: string;
        description: string;
        confirmationButtonText: string;
    };
    lastLogin: string;
    loanAccount: string;
    loanDetails: {
        error: string;
        header: string;
        nameOfLoan: string;
        loanOwners: string;
        ownerOfLoan: string;
        coOwner: string;
        noLoan: string;
        interest: string;
        paymentPlanMessage: string;
        dueDay: string;
        endDate: string;
        balance: string;
        principal: string;
        administrationFee: string;
        everyMonth: string;
        amount: string;
        nominalInterst: string;
        effectiveInterest: string;
        nextPayment: string;
        initialAmount: string;
        plannedDownpaymentDate: string;
        debitAccount: string;
        downpaymentDay: string;
        installmentAmount: string;
        editPaymentDateAndPaymentAccount: string;
        seeDetails: string;
        moreInformationAboutThisLoan: string;
    };
    downpayment: {
        cannotDoDownpayment: string;
        noTransferFromAccount: string;
    };
    loanError: string;
    loanDownpaid: string;
    loanOwners: string;
    loanName: string;
    loanOverviewErrorMessage: string;
    loanOverviewNoLoansHeader: string;
    loanOverviewNoLoansText: string;
    loanRepaymentPlan: {
        viewRepaymentPlan: string;
        error: string;
        period: string;
        youPay: string;
        instalment: string;
        charges: string;
        interest: string;
        balance: string;
    };
    loanEdit: {
        edit: string;
        editAccountToPayFrom: string;
        editDebitAccount: string;
        chooseDebitAccount: string;
        dayEachMonth: string;
        dayEachYear: string;
        dayEachHalfYear: string;
        dayEachCustom: string;
        editDay: string;
        chooseDay: string;
        save: string;
        everyDay: string;
        dayPrefix: string;
        onlyFutureDates: string;
        editInfoMessage: string;
        cantBeChanged: string;
        errors: {
            debitAccountDefaultError: string;
            paymentDayDefaultError: string;
            dayBiggerThanOrLessThan: string;
            mustChooseDate: string;
            alreadyChosen: string;
            biggerThan: string;
            biggerThanAndNotPaid: string;
            lessThan: string;
        };
    };
    logout: string;
    login: {
        toggleBiometrics: string;
        biometricsError: string;
        changeBank: string;
        changeBankAndAgreement: string;
        changeAgreement: string;
        myActivations: string;
        loginAndSigning: string;
        signingAndLoginChoice: string;
        signingAndLoginChoiceDifferent: string;
        stepUp: {
            title: string;
            description: string;
        };
    };
    loginAndSigning: {
        chooseLoginMethodDescription: string;
        chooseSigningMethodDescription: string;
        chooseSigningMethodTitle: string;
        step: string;
        chooseLogin: string;
        back: string;
        errorMessageBiometryDisabled: string;
        errorMessageSigningMethod: string;
        errorMessageNoSigningMethodChosen: string;
        errorMessageNoLoginMethodChosen: string;
        errorMessage: string;
        infoMessageEnableBiometry: string;
        headerSuccess: string;
        toFrontPage: string;
        saveAndContinue: string;
        saveAndComplete: string;
        signingMethods: Record<SigningMethodType, string>;
    };
    kidNumber: string;
    notEnoughInAccount: string;
    new: string;
    next: string;
    ok: string;
    payment: string;
    payNow: string;
    transferNow: string;

    applyForAvdragsfrihet: string;
    addPaymentForApproval: string;
    paymentForApproval: string;
    previous: string;
    paymentSettings: {
        eFaktura: string;
        avtaleGiro: string;
        addAndEditAgreements: string;
        accountView: string;
        editAccountView: string;
        paymentLimits: string;
        editPaymentLimits: string;
    };
    payWithBankId: string;
    priorityField: {
        title: string;
        label: string;
        description: string;
    };
    priorityOption: Record<CrossBorderPriority, string>;
    pushNotifications: {
        label: string;
        mainSwitch: string;
        error: string;
        errorFetchRules: string;
        errorRetry: string;
        errorDeactivate: string;
        errorToggleRule: string;
        chooseNotificationsToGet: string;
        soonMessage: string;
        infoMessage: string;
        gdprTitle: string;
        gdprDescription: string;
        location: string;
        category: {
            efaktura: string;
            messages: string;
            account: string;
            loan: string;
            payments: string;
        };
        toggleLabel: Record<NotificationType | 'account' | 'salary', string>;
        accountSpecificNotifications: Record<
            'BALANCE_LESS_THAN' | 'DEPOSIT_MORE_THAN',
            {
                title: string;
                text: string;
            }
        >;
        chooseAccountHere: string;
        account: {
            customNotifications: string;
            createNotificationRule: string;
            chooseNotificationType: string;
            chooseAccount: string;
            noAccountsToChooseFrom: string;
            youHaveNoMoreAccountsToAddThisNotificationTo: string;
            chooseAmount: string;
            errorCreatingNotification: string;
            createNotification: string;
            couldNotGetNotificationsForAccount: string;
            amountError: string;
        };
        deleteFlow: {
            areYouSure: string;
            deleteNotification: string;
        };
        salary: {
            title: string;
            tryingToChangeChosenAccount: string;
            editFailed: string;
            accountChanged: string;
            chooseSalaryAccount: string;
        };
        couldNotGetNotificationsFor: string;
    };
    reserved: string;
    amountNotAvailableWithDate: string;
    expectedBookBalanceDate: string;
    receiverTypes: {
        receiver: string;
        crossBorderReceiver: string;
    };
    receipt: {
        title: string;
        description: string;
        needReceipt: string;
        receiptType: Record<ReceiptType, string>;
        receiptTypeVerbose: Record<ReceiptType, string>;
        receiptTypeSimplified: Record<ReceiptType, string>;
    };
    recurringPayments: {
        recurringPayment: string;
        recurringTransfer: string;
        newRecurringTranfer: string;
        skipLink: string;
        recurringPayments: string;
        newRecurringPayment: string;
        agreements: string;
        errorMessage: string;
        noPayments: string;
        createPayment: string;
        createTransfer: string;
        recurringTransferSuccess: string;
        sureYouWantToDelete: string;
        isDeleted: string;
        update: string;
        lastDate: string;
        detailsButton: string;
        nextDate: string;
        frequency: string;
        amount: string;
        endDate: string;
        cantDeleteMessage: string;
    };
    recurringPaymentStatus: Record<RecurringPaymentStatus, string>;
    recurringPaymentType: Record<RecurringPaymentType, string>;
    recurringPaymentValidation: {
        success: string;
        error: {
            heading: string;
            description: string;
            check: string;
        };
        notSigned: {
            heading: string;
            description: string;
            tryAgain: string;
            change: string;
            cancel: string;
        };
    };
    salaryInfo: {
        title: string;
        editLink: string;
        error: string;
        untilNextSalary: string;
        payments: string;
        left: string;
        untilSalary: string;
        daysLeft: string;
        days: string;
        editSalaryInfoStartPageDescription: string;
        edit: {
            description: string;
            descriptionGdpr: string;
            confirmDelete: string;
            toFrontpageLink: string;
            updateSuccess: string;
            updateError: string;
            saveSuccess: string;
            saveError: string;
            deleteSuccess: string;
            deleteError: string;
            paydaysError: string;
            accountError: string;
            account: {
                placeholder: string;
                label: string;
                title: string;
                subtitle: string;
            };
            paydays: {
                placeholder: string;
                label: string;
                title: string;
                subtitle: string;
                lastDay: string;
                lastDayOfMonth: string;
                eachMonth: string;
                the: string;
                and: string;
            };
        };
    };
    save: string;
    settingsAccountOrderNeedMoreAccounts: string;
    settingsAccountFavoriteNeedMoreAccounts: string;
    settingsProfileLinkDescription: string;
    showLess: string;
    showMore: string;
    seeAll: string;
    seeAllAccounts: string;
    settings: {
        pushNotifications: string;
        changeAppIcon: string;
        login: string;
        paymentSettings: string;
        paymentSettingsSubtitle: string;
        nextSalary: string;
        nextSalarySubtitle: string;
        aboutApp: string;
        myConsents: string;
        minBolig: string;
        price: string;
        meMyselfAndI: string;
        adaptations: string;
        account: string;
        accountDescription: string;
        contactInfo: string;
        contactInfoBM: string;
        language: string;
        becomeCustomerChild: string;
    };
    thirdPartyDependencies: {
        title: string;
        description: string;
    };
    to: string;
    invalidFromAccount: {
        transferNotPossible: string;
        paymentNotPossible: string;
        transferBecauseItIs: string;
        chooseAnotherAccountTransfer: string;
        chooseAnotherAccountPayment: string;
    };
    invalidToAccount: string;
    missingToAccountTransfer: {
        title: string;
        tip: string;
        description: string;
        buttonText: string;
    };
    tooManyCharacters: string;
    tooManyCharactersKID: string;
    transfer: string;
    transferAmount: string;
    transferFromAccount: string;
    transferToAccount: string;
    tryAgain: string;
    transactionListNoTransactionsTitle: string;
    transactionListNoMoreTransactionsToLoadTitle: string;
    transactionListCouldNotGetTransactions: string;
    transactionListCouldNotLoadMoreTransactions: string;
    transactionResultInfoTransactionNotCompleteMessage: string;
    transactionSuccessPushNudge: {
        title: string;
        description: string;
        buttonText: string;
        abortButtonText: string;
    };
    viewEFaktura: string;
    waitOnDataErrorMessage: string;
    continue: string;
    paymentItem: {
        title: string;
        sureYouWantToUnapprove: string;
        sureYouWantToDelete: string;
        isDeleted: string;
        restartSuccess: string;
    };
    paymentFrom: string;
    payAmount: string;
    payFromAccount: string;
    accountNumber: string;
    accountName: string;
    accountOwners: string;
    paymentFromAccountName: string;
    paymentAddOwnText: string;
    transferAddOwnText: string;
    ownTextPlaceholder: string;
    paymentAddOwnTransactionText: string;
    kidHelpWithDash: {
        canHelp: string;
        addDash: string;
        gotHelp: string;
    };
    accountView: {
        noAccount: string;
        disposible: string;
        creditUnused: string;
        creditUsed: string;
    };
    receiverSearch: string;
    receiverIsAdded: string;
    receiverIsDeleted: string;
    receiverIsUpdated: string;
    receiversNotFound: string;
    search: string;
    name: string;
    addNewReceiver: string;
    addNewPayment: string;
    addReceiver: string;
    confirmDeleteReceiver: string;
    paymentValidation: {
        invalidAccountNumber: string;
        requiredField: string;
        existingAccountNumber: string;
        chooseFromAccount: string;
        chooseToAccount: string;
        receiverDontAcceptImmediatePayment: string;
        receiverDoesNotSupportImmediatePayment: string;
        chooseAmount: string;
        chooseValidDate: string;
        noCoverage: string;
        invalidKID: string;
        missingKID: string;
        missingNote: string;
        amountLowerThanMinimum: string;
        amountExceedsMaximum: string;
        amountExceedsMonthlyMaximum: string;
    };
    transactions: string;
    history: string;
    transactionDetails: {
        from: string;
        to: string;
        date: string;
        bookingDate: string;
        message: string;
        ownMessage: string;
        purpose: string;
        category: string;
        errorMessage: string;
        receiver: {
            address: string;
            bank: string;
            bicOrSwift: string;
        };
        bankReferenceNumber: string;
        kid: string;
        download: string;
        approvers: { createdBy: string; approvedBy: string };
        singleApprover: string;
        infoWillCome: string;
        multipleTransactionsDetails: string;
    };
    transactionTypes: Record<TransactionType, string>;
    transactionStatus: Record<TransactionStatus, string>;
    transactionCategories: Record<TransactionCategory, string>;
    transactionFilter: {
        search: string;
        freetextPlaceholder: string;
        filterTransactions: string;
        chooseCategory: string;
        categoryPlaceholder: string;
        chooseAmount: string;
        choosePeriod: string;
        from: string;
        to: string;
        fromDateError: DateInputErrors;
        toDateError: DateInputErrors;
        showHits: string;
        cancel: string;
        resetFilter: string;
        openModal: string;
        remove: string;
        editFilter: string;
        activeFilters: string;
        noMoreToLoad: string;
        noHits: string;
        reservedTransactionMessage: string;
        choose: string;
        period: string;
        amount: string;
        sentDirection: string;
        receivedDirection: string;
        sent: string;
        received: string;
        openSearch: string;
        lastMonths: string;
        lastMonth: string;
        goToAdvancedSearch: string;
        toTheTop: string;
        didYouNotFindWhatYouWereLookingFor: string;
        backToSearch: string;
        removeChosen: string;
    };
    transactionDownload: {
        open: string;
        close: string;
        downloadMenu: string;
        exportToPdf: string;
        exportToExcel: string;
        exportToCSV: string;
    };
    update: string;
    signing: {
        title: string;
    };
    clientError: Partial<Record<ErrorCodesBackend, ErrorDescription>>;
    dateField: {
        diffTimeZoneMessage: string;
        bankHolidayMessage: string;
        invalidMessage: string;
        requiredMessage: string;
        greaterThanDateMessage: string;
        lessThanDateMessage: string;
        title: string;
        today: string;
        paymentInterval: {
            alias: string;
            titlePayment: string;
            titleTransfer: string;
            titleEnd: string;
            lastPaymentDate: string;
            noLastPaymentDate: string;
            withEndDate: string;
        };
        recurringPaymentInterval: Record<PaymentInterval, string>;
        recurringPaymentDateInterval: Record<PaymentInterval, string>;
        endDates: Record<EndType, string>;
    };
    receiverNameOverride: string;
    toggleKid: string;
    toggleNote: string;
    amountNote: Record<
        NoteTypes,
        {
            label: string;
            placeholder: string;
        }
    >;
    payTo: string;
    chooseAccount: string;
    favoriteAccount: {
        favoriteAccountCardTitle: string;
        favoriteAccountCardButtonText: string;
        favoriteAccountCardDescription: string;
        favoriteAccountModalTitle: string;
        favoriteAccountModalDescription: string;
        saveFavoriteAccountButtonText: string;
        favoriteAccountSaved: string;
        frontpageFavoriteAccountButtonText: string;
        frontpageFavoriteAccountModalText: string;
        favioriteAccountIllustrationImageAltText: string;
    };
    dismissKeyboard: string;
    kidRequiredError: string;
    paymentInfoMessages: Partial<Record<PaymentType | 'downpayment' | 'efaktura-sharing', PaymentInfoMessage>>;
    bankidExpired: {
        header: string;
        nativeMessage: string;
        nativeMessage2: string;
        desktopMessage: string;
        settingsLink: string;
    };
    stopFuturePaymentInfoMessages: Partial<Record<PaymentType | 'downpayment', StopFuturePaymentInfoMessage>>;
    updateFuturePaymentInfoMessages: Partial<Record<PaymentType | 'downpayment', UpdateFuturePaymentInfoMessage>>;
    approvalItem: {
        overdue: string;
        overdueToday: string;
        deadline: string;
        sharedBy: string;
    };
    accountDetails: {
        seeDetails: string;
        moreInfoAboutThisAccount: string;
        seeAccountDetails: string;
        copyAccountNumber: string;
        copyIBAN: string;
        cardsConnectedToTheAccount: string;
        card: string;
        accountName: string;
        accountDetailsErrorMessage: string;
        mainOwner: string;
        owner: string;
        accountAccess: string;
        addAuthorizedCustomer: {
            responsibility: string;
            shareAccount: string;
            chooseWho: string;
            mustBeCustomer: string;
            mustBeCustomerWarning: string;
            confirmText: string;
            ssid: string;
            elevenCharacters: string;
            ssidError: string;
            fillName: string;
            fullname: string;
            nameError: string;
            nextButton: string;
            backButton: string;
            shareAccountButton: string;
            shareSuccess: string;
            doneButton: string;
            deleteConfirmationQuestion: string;
            deleteConfirmationButton: string;
            deleteCancelButton: string;
            deleteConfirmation: string;
            confirmTitle: string;
            termsTitle: string;
            termsSharingAccountInfo: string;
            termsSharingAccountInfoList: {
                itemOne: string;
                itemTwo: string;
                itemThree: string;
                itemFour: string;
                itemFive: string;
            };
            termsIncludingInfo: string;
            termsIncludingInfoList: {
                itemOne: string;
                itemTwo: string;
                itemThree: string;
            };
            deleteAuthorizedCustomer: string;
        };
        details: string;
        openedDate: string;
        iban: string;
        bic: string;
        sepaBic: string;
        routingBic: string;
        couldNotSave: string;
        maxCharacters: string;
        accountType: string;
        accountProductName: string;
        mappedAccountType: Record<AccountType, string>;
        orgContact: string;
    };
    accountsList: {
        errorMessage: string;
        noAccountsHeader: string;
        noAccountsDescription: string;
        noAccountsDescriptionWithButton: string;
        createAccount: string;
        createNewAccount: string;
        infoOnAccounts: string;
        totalBalance: string;
        clauseWithoutForeignCurrency: string;
        emptyAccountsGroup: string;
    };
    accountInterest: {
        header: string;
        types: Record<
            InterestType,
            {
                interest: string;
                above: string;
                upTo: string;
            }
        >;
        overdraftInterest: string;
        grantedOverdraftInterest: string;
        notYetCapitalizedInterest: string;
    };
    invoiceArchive: {
        noInvoices: string;
        documentType: Record<InvoiceType, string>;
        status: Record<InvoiceStatus, string>;
    };
    profile: {
        myProfile: string;
        messages: string;
        letters: string;
        switchBank: string;
        switchBankAndAgreement: string;
        switchAgreement: string;
        unreadMessagesSingle: string;
        unreadMessagesplural: string;
        agreementError: string;
    };
    periodicControl: {
        intro: string;
        correctContactInfo: string;
        noEdit: string;
        yesCorrect: string;
        thanks: string;
        howToEdit: string;
        backToMobileBank: string;
        backToNettbank: string;
        error: string;
        errorTitle: string;
        cancel: string;
        tryAgain: string;
    };
    contactInfo: {
        editContactInfo: string;
        contactInfo: string;
        email: string;
        mobile: string;
        address: string;
        emailMissing: string;
        mobileMissing: string;
        addressMissing: string;
        save: string;
        success: string;
        messages: string;
        signingError: {
            message: string;
            contactMessage: string;
        };
        stepUp: {
            text: string;
            link: string;
        };
        warnings: {
            email: string;
            mobile: string;
            mobileDesktop: string;
            address: string;
        };
        errors: {
            foundNoInfo: string;
            wrongEmailFormat: string;
            wrongMobileFormat: string;
            emailNoChange: string;
            mobileNoChange: string;
        };
    };
    companyInfo: {
        generalManagerNotFound: string;
        editCompanyInfo: string;
        editDescription: string;
        editNameWarning: string;
        companyInformation: string;
        companyName: string;
        organisationNumber: string;
        address: string;
        generalManager: string;
        editGeneralManager: string;
        cannotEditGeneralManager: string;
        updateSuccess: string;
        errors: {
            foundNoBusinessInformation: string;
            invalidEmail: string;
            invalidMobile: string;
        };
    };
    updatePaymentLimits: {
        validation: {
            cannotExceedMonthly: string;
            bankRestricted: string;
            bankRestrictedMonthly: string;
            fillInField: string;
        };
        result: {
            rejectedTitle: string;
            rejectedInfo: string;
            check: string;
            success: string;
            close: string;
            toFrontPage: string;
        };
        stepUpRequired: {
            title: string;
            text: string;
            text2: string;
            stepUpButton: string;
        };
        mobileRiskyDevice: {
            title: string;
            text: string;
            text2: string;
            buttonText: string;
        };
        desktopRiskyDevice: {
            title: string;
            text: string;
            text2: string;
        };
        missingRights: string;
        maxTransactionAmount: string;
        maxMonthlyTransactionAmount: string;
        update: string;
        currentlyYouHavePaid: string;
    };
    closeWindow: string;
    withdrawals: {
        infoAboutNumberOfWithdrawalsLeft: string;
        oneWithdrawalLeft: string;
        noWithdrawalsLeft: string;
        freeWithdrawals: string;
        freeWithdrawalsOf: string;
        withdrawalPeriods: Record<AccountWithdrawalPeriod, string>;
    };
    kredittbanken: {
        category: Record<Category, string>;
        kycInfo: string;
    };
    openPaymentWindow: string;
    documentsForSigning: string;
    loanApplications: {
        status: Record<LoanApplicationStatus, string>;
        type: Record<LoanApplicationType, string>;
        needsAction: string;
    };
    importantTask: string;
    importantTasks: string;
    updateToSwitchLanguage: string;
    unknownAccountWarning: string;
    unknownAccount: string;
    futurePaymentLimitWarning: string;
    goToTop: string;
    goBack: string;
    tasks: string;
    otherTasks: string;
    edit: string;
    onlyOneAccount: string;
    accountNeedsMultipleApproversTransactionInfoMessage: string;
    more: string;
    moveAvtaleGiro: {
        whenYouStartTheProcess: string;
        quickAndIntuitive: string;
        startProcess: string;
        notaBene: string;
        notaBeneDescription: string;
        moveAvtaleGiro: string;
        chooseAvtaleGiros: string;
        moveOn: string;
        newHome: string;
        chooseAvtaleGiroPaymentAccount: string;
        avtaleGirosInOtherBanks: string;
        areYouSure: string;
        movedByMistake: string;
        yesMoveAvtaleGiro: string;
        moveHasStarted: string;
        toAvtaleGiroPage: string;
        removeChosen: string;
        chooseAll: string;
        confirmation: string;
        toFrontPage: string;
        backToTheFrontpage: string;
        ofChosen: string;
        summary: string;
        youAreReadyToMove: string;
        moveAvtaleGirosNow: string;
        foundNoAvtaleGiros: string;
        someAvtaleGirosMightHaveANewMaxAmount: string;
        errors: {
            debitAccountNotSelected: string;
            noAvtaleGirosSelected: string;
            couldNotFindExternalAvtaleGiros: string;
            couldNotMoveSomeOrAllAvtaleGiros: string;
            accountsCouldNotBeFetched: string;
            noValidAccounts: string;
            ifThisIsWrongContactUs: string;
        };
        rejected: {
            heading: string;
            description: string;
        };
    };
    accountIsBlocked: string;
    messages: {
        oldMessagesWillBeRemoved: string;
        ifYouWishToSaveThem: string;
        messagesText: string;
    };
    psd2Warning: string;
    approveMultipleEFaktura: {
        approveEFaktura: string;
        toTheFrontpage: string;
        seeEFaktura: string;
        eFakturasWaiting: string;
        approveAndPayNEFaktura: string;
        subtotal: string;
        doYouWantToApproveAll: string;
        youAreApprovingNEFakturasAtOnce: string;
        youAreApprovingNEFakturasWithSharedAtOnce: string;
        choosePaymentAccount: string;
        allEFakturasArePaidFromThisAccount: string;
        doYouWishToPayFromDifferentAccounts: string;
        approveNow: string;
        error: BatchEFakturaErrorMessages;
        signingResult: {
            error: {
                nEFakturaWasNotApproved: string;
                looksLikeWeCouldNotApproveAllYourEFaktura: string;
            };
            success: {
                allEFakturasWasApproved: string;
                eFakturasArePaidOnDueDate: string;
            };
        };
    };
    commonErrors: {
        errorGettingAccounts: string;
        gettingAccountsFailed: string;
        tryLoadingAgain: string;
        copyRCID: string;
        paymentAndNextSalaryFailed: string;
        paymentFailed: string;
        allPaymentServicesIsAffected: string;
        gettingEFakturaFailed: string;
        thereIsAnErrorOnOurSide: string;
    };
}

export enum InterestType {
    CREDIT,
    DEBIT,
}

export type ErrorCodesBackend = AppError | TietoEvryEraEsbWsError;

export interface ErrorDescription {
    title: string;
    description: string;
}

export interface ErrorsConfigType {
    somethingWentWrong: string;
    somethingWentWrongFetchingReceivers: string;
    somethingWentWrongFetchingSomeReceivers: string;
    somethingWentWrongDeletingReceiver: string;
    somethingWentWrongChangingReceiver: string;
    avtaleGiroErrorFetchingAccounts: string;
    oops: string;
    ohNo: string;
    sorry: string;
    defaultMessage: string;
    rcid: string;
}
